import React, { useEffect, useContext, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import Spinner from "../components/Spinner";
import SignInErrorAlert from "../components/SignInErrorAlert";
import { HttpError } from "../services/HttpError";
import { describeSignInError } from "../utils/signInErrors";

/**
 * OAuth callback for "Sign in with Starek". Cognito's Hosted UI lands here
 * with either `?code=&state=` or, when it declined the sign-in, with
 * `?error=&error_description=&state=` - the second form used to be posted
 * to the API as `code: undefined` and reported as a generic failure.
 */
function SSOLogin() {
    const [searchParams] = useSearchParams();

    const starekContext = useContext(StarekContext);

    const navigate = useNavigate();
    const [message, setMessage] = useState(
        "Please wait while we sign you in..."
    );
    const [signInError, setSignInError] = useState(null);

    const [loading, setLoading] = useState(true);
    const didRun = useRef(false);
    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;

        async function handleSSOLogin() {
            try {
                const ssoPayload = Object.fromEntries(searchParams.entries());
                const { code, state, error, error_description } = ssoPayload;
                const callbackUrl = window.location.href.split("?")[0];

                if (error) {
                    throw new HttpError(
                        error_description ||
                            `The sign-in provider declined the sign-in (${error}).`,
                        {
                            code: "SSO_PROVIDER_REJECTED",
                            details: {
                                provider_error: error,
                                ...(error_description
                                    ? { provider_error_description: error_description }
                                    : {}),
                            },
                            url: callbackUrl,
                        }
                    );
                }

                let parsedState;
                try {
                    parsedState = JSON.parse(state);
                } catch {
                    parsedState = null;
                }
                if (!parsedState?.workspace) {
                    throw new HttpError("Missing state in SSO callback", {
                        code: "SSO_STATE_MISSING",
                        url: callbackUrl,
                    });
                }
                const { workspace, returnUriHost, returnUriPath } = parsedState;

                const response = await starekContext.ssoLogin({
                    code,
                    workspace,
                    returnUriHost,
                    returnUriPath,
                });
                if (!response.token) {
                    throw new HttpError("No token received from IDP!", {
                        code: "SSO_TOKEN_INVALID",
                        url: callbackUrl,
                    });
                }

                navigate(returnUriPath || "/");
            } catch (e) {
                console.error("[SSOLogin] sign-in failed:", e);
                setMessage("There was a problem signing in");
                setSignInError(describeSignInError(e));
            } finally {
                setLoading(false);
            }
        }
        handleSSOLogin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="login-page">
            <div
                className="d-flex flex-column align-items-center justify-content-center text-white"
                style={{ minHeight: "60vh" }}
            >
                <h4>{message}</h4>
                <br />
                {loading && <Spinner />}
                {signInError && (
                    <div style={{ width: "min(480px, 92vw)" }}>
                        <SignInErrorAlert error={signInError} />
                        <button
                            className="btn btn-primary btn-block"
                            onClick={() => navigate("/login")}
                        >
                            Retry Login
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SSOLogin;
